import {gql} from '@apollo/client';
import React, {useState, useRef} from 'react';

import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import MetadataTableHeader from 'metadata/MetadataTableHeader';
import NotificationScheduleEditor from 'notifications/notification-schedule/NotificationScheduleEditor';
import {DeliveryDirection} from 'notifications/notification-schedule/NotificationScheduleEditorRow';
import {
  EvaluatorVersion,
  NotificationSchedule,
  RecipientType,
} from 'notifications/notification-schedule/NotificationScheduleExpressionUtils';
import {updateNotificationSchedules} from 'studies/StudyDAO';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {COLORS} from 'ui-library/ColorPickerConstants';
import ErrorMessage from 'ui-library/ErrorMessage';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {StudyNotificationSchedulesQuery} from './__generated/StudyNotificationSchedulesQuery';

async function updateNotifications(
  studyId: string,
  newNotificationSchedules: Array<NotificationSchedule>
): Promise<void> {
  await updateNotificationSchedules(studyId, {schedules: newNotificationSchedules});
}

export function EditableNotifications({
  studyId,
  existingNotificationSchedules,
  isWritable,
}: {
  studyId: string;
  existingNotificationSchedules: Array<NotificationSchedule>;
  isWritable: boolean;
}): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [notificationSchedules, setNotificationSchedules] = useState(existingNotificationSchedules);

  const defaultDeliveryTime = {second: 0, minute: 0, hour: 0};
  const defaultDeliveryDate = {days: 1, direction: DeliveryDirection.BEFORE};
  const defaultRecipient = RecipientType.ASSIGNEE;

  const notificationSchedulesRef = useRef(notificationSchedules);

  return (
    <div>
      <MetadataTableHeader
        isEditable={isWritable}
        isEditing={isEditing}
        title="Notification schedules"
        headerContent={
          notificationSchedules.length === 0 ? (
            <div style={{color: COLORS.GRAYS[4]}}>
              This study does not have any notification schedules configured.
            </div>
          ) : null
        }
        onEdit={() => {
          notificationSchedulesRef.current = notificationSchedules;
          setIsEditing(true);
        }}
        handleCancelEdit={() => {
          setIsEditing(false);
          setNotificationSchedules(notificationSchedulesRef.current);
        }}
        handleCompleteEdit={async () => {
          await updateNotifications(studyId, notificationSchedules);
          setIsEditing(false);
        }}
      />
      <NotificationScheduleEditor
        notificationSchedules={notificationSchedules}
        defaultDeliveryTime={defaultDeliveryTime}
        defaultDeliveryDate={defaultDeliveryDate}
        defaultRecipient={defaultRecipient}
        onUpdate={(notificationSchedules: Array<NotificationSchedule>) => {
          setNotificationSchedules(notificationSchedules);
        }}
        readonly={!isEditing}
      />
    </div>
  );
}

export const STUDY_NOTIFICATION_SCHEDULES_QUERY = gql`
  query StudyNotificationSchedulesQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
        notificationSchedules {
          deliveryDateExpr
          recipientExpr
          evaluatorVersion: scheduleEvalVersion
        }
      }
    }
  }
`;

function QueryingEditableNotifications({
  studyId,
  isWritable,
}: {
  studyId: string;
  isWritable: boolean;
}): JSX.Element | null {
  const queryResult = useSkippableBenchlingQuery<StudyNotificationSchedulesQuery>(
    STUDY_NOTIFICATION_SCHEDULES_QUERY,
    {
      variables: {studyId},
      fetchPolicy: 'cache-and-network',
      skip: !BenchlingConfig.STUDIES_ENABLE,
    }
  );

  if (queryResult.type === QueryResultType.SKIPPED) {
    return null;
  }

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (queryResult.type === QueryResultType.ERROR) {
    return <ErrorMessage error="Failed to load notification schedule" />;
  }

  if (isInaccessible(queryResult.data.study)) {
    return <div>You do not have permission to view this item.</div>;
  }

  const study = queryResult.data.study;
  const adapter = getTypedStudyAdapter(study.studySchema.studyType);
  if (
    !(adapter.isNotificationSchedulesEnabled && isFeatureEnabled({feature: ProcessesFeature.PROCESSES_OPEN}))
  ) {
    return null;
  }

  const existingNotificationSchedules: Array<NotificationSchedule> = study.notificationSchedules.map(
    (notificationSchedule) => ({
      recipientExpr: notificationSchedule.recipientExpr,
      deliveryDateExpr: notificationSchedule.deliveryDateExpr,
      evaluatorVersion: EvaluatorVersion.V1,
    })
  );

  return (
    <EditableNotifications
      studyId={studyId}
      existingNotificationSchedules={existingNotificationSchedules}
      isWritable={isWritable}
    />
  );
}

export default QueryingEditableNotifications;
